import {
  Component,
  OnInit,
  ViewChild,
  ChangeDetectorRef,
  ElementRef,
  Renderer2,
  Output,
  EventEmitter,
  ChangeDetectionStrategy,
} from '@angular/core';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, switchMap } from 'rxjs/operators';
import { TargetsService } from '@app/shared/services/targets.service';
import { PopoverComponent } from '@app/shared/components/popover/popover.component';
import { PopoverService } from '@app/shared/services/popover.service';
import { A11yService } from '@app/shared/services/a11y.service';
import { isKey, Key } from '@app/shared/key';
import { TranslateService } from '@ngx-translate/core';
import { TagsSearchItem } from '@app/tags/tags';
import { OnboardingStateService } from '@app/onboarding/services/onboarding-state.service';
import { OnboardingService } from '@app/onboarding/services/onboarding.service';
import { Skill } from '@app/onboarding/onboarding.model';
import { TrackerService } from '@app/shared/services/tracker.service';
/**
 * Skills Search component
 * This component allows the user to search for skills and add a skill from the dropdown
 *
 * @param toggleSkill {EventEmitter boolean} emits to the parent that a skill was selected
 *
 * @example
 * ```
 * <dgx-skill-search (toggleSkill)="onToggleSkill($event)"
 * </dgx-skill-search>
 * ```
 */
@Component({
  selector: 'dgx-skill-search',
  templateUrl: './skill-search.component.html',
  styleUrls: ['./skill-search.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SkillSearchComponent implements OnInit {
  @Output()
  public toggleSkill: EventEmitter<boolean> = new EventEmitter<boolean>();
  @ViewChild('skillSearchInput') public skillSearchInputRef: ElementRef;
  @ViewChild('popoverComponent') public popoverComponent: PopoverComponent;
  @ViewChild('popoverComponentCustomTag')
  public popoverComponentCustomTag: PopoverComponent;
  public skillSearchTerm: string = '';
  public isPopoverOpen: boolean = false;
  public suggestions: TagsSearchItem[] = [];
  public i18n = this.translateService.instant([
    'TargetResourcesForm_NoResults',
    'Onboarding_AddedSkill',
    'Onboarding_SearchSkills_SearchForSkills',
  ]);
  public loadingResults: boolean = false;
  public shouldShowCustomTag: boolean = false;
  public shouldShowTagDropdown: boolean = false;
  public customItem: any;

  private unlisteners: (() => void)[] = [];
  private suggest = new Subject<any>();

  constructor(
    private targetsService: TargetsService,
    private renderer: Renderer2,
    private popoverService: PopoverService,
    private a11yService: A11yService,
    private cdr: ChangeDetectorRef,
    private translateService: TranslateService,
    private onboardingStateService: OnboardingStateService,
    private onboardingService: OnboardingService,
    private trackerService: TrackerService
  ) {}

  public ngOnInit(): void {
    this.skillSearchTerm = null;

    this.suggest
      .pipe(
        debounceTime(300),
        distinctUntilChanged(), // don't retrigger unless term has changed
        switchMap((skillSearchTerm) => {
          // term must have at least two characters
          if (skillSearchTerm.length > 1) {
            this.loadingResults = true;
            this.cdr.detectChanges();
            return this.targetsService.getSuggestedTitles(
              'Skill',
              skillSearchTerm,
              10
            );
          }
          this.shouldShowCustomTag = false;
          this.shouldShowTagDropdown = false;
          this.cdr.detectChanges();
          return [];
        })
      )
      .subscribe((s) => {
        this.shouldShowCustomTag = s.length === 0 && !!this.skillSearchTerm;
        if (this.shouldShowCustomTag) {
          this.customItem = {
            name: this.skillSearchTerm,
            title: this.skillSearchTerm,
          };
        }
        this.suggestions = s;
        this.shouldShowTagDropdown = this.suggestions?.length > 0;
        this.loadingResults = false;
        this.cdr.detectChanges();
      });
  }

  /**
   * Focus on tag search element
   */
  public focusSkillSearch(): void {
    this.skillSearchInputRef?.nativeElement.focus();
  }

  /**
   * Get next suggestion from search value entered
   * @param value search term entered
   */
  public onInputChange(event: Event) {
    this.suggest.next((<HTMLInputElement>event.target).value);
  }

  /**
   * Add a skill from the search.
   *
   *  @param newSkill Job role to be added to grid and selected
   */
  public addSkill(newSkill: TagsSearchItem): void {
    // clear search term
    this.skillSearchTerm = '';
    const displaySkills: any = this.onboardingStateService.addedTags;
    const onboardingUserAddedTags: any =
      this.onboardingStateService.onboardingUserAddedTags;
    const profileTags: any = this.onboardingStateService.currentProfileSkills;
    const alltags = [
      ...profileTags,
      ...onboardingUserAddedTags,
      ...displaySkills,
    ];

    // Check if this new skill already exists in the display list
    const displayedButtons = !!alltags
      ? alltags.filter(
          (tag) => tag.name.toLowerCase() === newSkill.name.toLowerCase()
        )
      : [];

    const alreadyDisplayed: boolean = displayedButtons.length > 0;

    // if it does not exist yet in the displayed tags add tag to the featured display list and emit to parent
    if (!alreadyDisplayed) {
      const tagObject: Skill = {
        name: newSkill.name,
        title: newSkill.title,
        tagId: newSkill.id,
        isFollowing: true,
        source: this.i18n.Onboarding_AddedSkill,
      };
      this.onboardingService.addUserAddedSkill(tagObject);
      this.trackerService.trackEventData({
        action: 'Onboarding Skill Selected',
        category: 'Onboarding',
        properties: {
          Location: 'Onboarding Search Skills',
          SkillId: tagObject.tagId,
          SkillName: tagObject.name,
        },
      });
      // if there are no similar skills when a user searches and addes a skill
      // try to get similar skills
      if (this.onboardingStateService.similarTags.length === 0) {
        this.onboardingService.updateSimilarSkills(
          this.onboardingStateService.addedTags
        );
      } else {
        // if there are similar skills check to see if the added skill is in the similar displayed list
        // and remove it from the similar list if it is
        const displayedSimilarIndex =
          this.onboardingStateService.displayedSimilarTags.findIndex(
            (tag) => tag.name.toLowerCase() === newSkill.name.toLowerCase()
          );

        // if the tag is alreadu in the display list
        if (displayedSimilarIndex > -1) {
          // remove the skill from the displayed similar list
          this.onboardingStateService.displayedSimilarTags.splice(
            displayedSimilarIndex,
            1
          );

          this.onboardingStateService.updateDisplayedSimilarTags(
            this.onboardingStateService.displayedSimilarTags
          );
          // If that was the last displayed skill try and load more skills if there are any
          if (!this.onboardingStateService.displayedSimilarTags.length) {
            this.onboardingService.loadMoreSimilarSkills();
          }
        }
      }
    } else {
      // if it was already added annouce that the still is already in the review list
      this.a11yService.announcePolite(
        this.translateService.instant('Onboarding_A11ySkillAlreadyAdded', {
          skillName: newSkill.title,
        })
      );
    }
  }

  public onIsPopoverOpenChange(isOpen: boolean, element: PopoverComponent) {
    this.isPopoverOpen = isOpen;
    if (isOpen) {
      this.unlisteners.push(
        this.renderer.listen(
          this.skillSearchInputRef.nativeElement,
          'keydown',
          (ev) => {
            // on user tabbing close popover and go to next tabbable element
            if (isKey(ev, Key.Tab)) {
              element.preventRefocus = true;
              this.popoverService.close();
            }
            if (isKey(ev, Key.Down)) {
              ev.preventDefault();
              this.a11yService.focusNextFocusable(
                element.popover.nativeElement
              );
            }
          }
        )
      );
    } else {
      this.removeListeners();
    }
  }

  private removeListeners() {
    for (const unlisten of this.unlisteners) {
      unlisten();
    }
    this.unlisteners = [];
  }
}
